import { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import type { Workspace } from "../api/contract";
import { servingProblem, type ServingController } from "../hooks/useServingChange";
import { describeAction } from "../lib/actionCopy";
import { LEGACY_BLOG_KINDS } from "../lib/changes";
import { displayOrigin } from "../lib/workspaceState";
import { Spinner } from "./Spinner";

/** A saved version of the draft, or a publication, as the owner sees it in History. */
export interface HistoryEntry {
  id: string;
  title: string;
  detail: string;
  day: string;
  /** The draft this change produced; absent for publications. */
  draftId: string | null;
  kind: "current" | "version" | "live" | "published";
  /** For a publish or rollback: the published version it made live, which can be put live again. */
  releaseId: string | null;
  /** The draft this change started from; the design or brand of before the change can come back from it. */
  baseDraftId: string | null;
  /** The change replaced the design, or changed the brand guidelines. */
  design: boolean;
  brand: boolean;
}

/** Parts of an earlier version that can come back on their own. */
export type RestorePart = "design" | "brand";

const PUBLICATIONS = new Set(["publish", "rollback", "unpublish"]);

/** What each kind of change did, in the past tense. */
const DONE: Record<string, string> = {
  "bundle.bootstrap": "First draft built",
  "content.patch": "Text changed",
  "content.edit": "Text changed",
  "content.patch-instance": "Text changed",
  "content.add": "Content added",
  "content.delete": "Content deleted",
  "content.restore": "Content restored",
  "media.add": "Photos added",
  "bundle.replace": "New design applied",
  "design.restore": "Design restored",
  "draft.restore": "Earlier version restored",
  "brand.set": "Brand guidelines updated",
  "brand.restore": "Brand restored",
  "site.set": "Site details updated",
};

function dayOf(time: Date, now: Date): string {
  const start = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((start(now) - start(time)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return time.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/**
 * Every applied change that produced a version, newest first, plus publications. Failed, cancelled
 * and pending actions are left out: they never changed the site.
 */
export function historyEntries(workspace: Workspace, now = new Date()): HistoryEntry[] {
  const entries: HistoryEntry[] = [];
  for (const action of workspace.actions) {
    if (action.state !== "succeeded" || LEGACY_BLOG_KINDS.includes(action.kind)) continue;
    const draftId = typeof action.result?.draftId === "string" ? action.result.draftId : null;
    const publication = PUBLICATIONS.has(action.kind);
    if (!publication && !draftId) continue;
    const time = action.createdAt ? new Date(action.createdAt) : null;
    const who = publication ? null : action.by === "admin" ? "Stax" : "you";
    const detail = [
      time ? time.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : null,
      action.subject?.label ?? null,
      who,
    ].filter(Boolean).join(" · ");
    const releaseId = publication && action.kind !== "unpublish" && typeof action.result?.releaseId === "string" ? action.result.releaseId : null;
    const live = publication && action.kind !== "unpublish" && typeof action.result?.releaseId === "string" && action.result.releaseId === workspace.site.activeReleaseId;
    entries.push({
      id: action.id,
      title: publication ? describeAction(action).detail : DONE[action.kind] ?? describeAction(action).title,
      detail,
      day: time ? dayOf(time, now) : "Earlier",
      draftId: publication ? null : draftId,
      kind: publication ? (live ? "live" : "published") : draftId === workspace.site.draftId ? "current" : "version",
      releaseId,
      // A first draft has nothing before it, and a change that started from its own result changed nothing to go back to.
      baseDraftId: !publication && action.kind !== "bundle.bootstrap" && action.baseDraftId && action.baseDraftId !== draftId ? action.baseDraftId : null,
      design: !publication && (action.changes ? action.changes.design : action.kind === "bundle.replace"),
      brand: !publication && (action.changes ? action.changes.brand : action.kind === "brand.set"),
    });
  }
  // Only the newest change that produced the current draft is "current".
  let currentSeen = false;
  for (const entry of entries) {
    if (entry.kind !== "current") continue;
    if (currentSeen) entry.kind = "version";
    currentSeen = true;
  }
  return entries;
}

interface HistoryViewProps {
  workspace: Workspace;
  /** The version on the canvas, if the owner is previewing one. */
  previewing: string | null;
  onPreview: (entry: HistoryEntry) => void;
  /** Changes already supplies the surrounding title and explanation. */
  compact?: boolean;
  /** Puts an earlier published version live. Absent, the rows have no such button. */
  serving?: ServingController;
  /** Brings back the design or brand from before a change; resolves whether it applied. */
  onRestorePart?: (entry: HistoryEntry, part: RestorePart) => Promise<boolean>;
}

type Asked = { id: string; ask: "live" | RestorePart };

const ASKS: Record<Asked["ask"], { question: string; yes: string; busy: string }> = {
  live: { question: "Put this version live? Visitors will see it straight away, and your draft stays as it is.", yes: "Put it live", busy: "Putting it live…" },
  design: { question: "Go back to the design from before this change? Your text, photos and brand stay as they are.", yes: "Restore design", busy: "Restoring…" },
  brand: { question: "Go back to the brand guidelines from before this change? Your text, photos and design stay as they are.", yes: "Restore brand", busy: "Restoring…" },
};

/** Every version of the site: preview any of them, and bring one back from its preview. */
export function HistoryView({ workspace, previewing, onPreview, compact = false, serving, onRestorePart }: HistoryViewProps) {
  const entries = historyEntries(workspace);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [asked, setAsked] = useState<Asked | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const confirm = async (entry: HistoryEntry, ask: Asked["ask"]) => {
    setBusy(true);
    setFailed(null);
    const done = ask === "live" ? await serving?.run({ kind: "rollback", releaseId: entry.releaseId! }) : await onRestorePart?.(entry, ask);
    setBusy(false);
    if (done) { setAsked(null); setMenuFor(null); } else if (ask !== "live") setFailed(entry.id);
  };
  if (entries.length === 0) return <p className="bw-activity-empty">Nothing yet. Every change you and Stax make will be kept here.</p>;
  const days = [...new Set(entries.map((entry) => entry.day))];
  const liveUrl = workspace.manifest.canonicalOrigin;
  return (
    <div className="bw-library">
      {!compact && <p className="bw-library-intro">Every change is kept. Preview any version, or bring it back.</p>}
      {days.map((day) => (
        <section key={day} aria-label={day}>
          <h3 className="bw-library-title">{day}</h3>
          <ul className="bw-items">
            {entries.filter((entry) => entry.day === day).map((entry) => (
              <li key={entry.id} className="bw-item" data-open={previewing !== null && previewing === entry.draftId ? true : undefined}>
                <div className="bw-item-row">
                  <div className="bw-item-head">
                    <span className="bw-item-label">{entry.title}</span>
                    <span className="bw-item-meta">{entry.detail}</span>
                  </div>
                  {entry.kind === "current" && <span className="bw-history-tag">Current</span>}
                  {entry.kind === "live" && (
                    <a className="bw-btn bw-btn--quiet" href={liveUrl} target="_blank" rel="noreferrer">
                      View live<span className="bw-sr"> at {displayOrigin(liveUrl)} (opens in a new tab)</span>
                    </a>
                  )}
                  {entry.kind === "version" && (
                    <button type="button" className="bw-btn bw-btn--quiet" onClick={() => onPreview(entry)} aria-pressed={previewing === entry.draftId}>
                      {previewing === entry.draftId ? "Showing" : "Preview"}
                    </button>
                  )}
                  {entry.kind === "published" && entry.releaseId && serving && (
                    <button type="button" className="bw-btn bw-btn--quiet" disabled={busy} onClick={() => { setAsked({ id: entry.id, ask: "live" }); setFailed(null); serving.clear(); }}>Put this version live</button>
                  )}
                  {onRestorePart && entry.baseDraftId && (entry.design || entry.brand) && (entry.kind === "version" || entry.kind === "current") && (
                    <button type="button" className="bw-btn bw-btn--icon" aria-label={`More for ${entry.title}`} aria-expanded={menuFor === entry.id} disabled={busy}
                      onClick={() => { setMenuFor(menuFor === entry.id ? null : entry.id); setAsked(null); setFailed(null); }}>
                      <MoreHorizontal size={18} aria-hidden="true" />
                    </button>
                  )}
                </div>
                {menuFor === entry.id && !asked && (
                  <div className="bw-history-menu" role="group" aria-label={`Go back on ${entry.title}`}>
                    {entry.design && <button type="button" className="bw-btn bw-btn--quiet" onClick={() => setAsked({ id: entry.id, ask: "design" })}>Restore this design</button>}
                    {entry.brand && <button type="button" className="bw-btn bw-btn--quiet" onClick={() => setAsked({ id: entry.id, ask: "brand" })}>Restore this brand</button>}
                  </div>
                )}
                {asked?.id === entry.id && (
                  <div className="bw-discard-confirm" role="group" aria-label={ASKS[asked.ask].yes}>
                    <span>{ASKS[asked.ask].question}</span>
                    <button type="button" className="bw-btn bw-btn--primary" disabled={busy} onClick={() => void confirm(entry, asked.ask)}>
                      {busy && <Spinner />}
                      {busy ? ASKS[asked.ask].busy : ASKS[asked.ask].yes}
                    </button>
                    <button type="button" className="bw-btn bw-btn--quiet" disabled={busy} onClick={() => setAsked(null)}>Cancel</button>
                  </div>
                )}
                {asked?.id === entry.id && asked.ask === "live" && serving && servingProblem(serving.state) && <p className="bw-change-error" role="alert">{servingProblem(serving.state)}</p>}
                {failed === entry.id && <p className="bw-change-error" role="alert">That didn’t work. Something changed after that version, so it can’t come back on its own. Nothing was changed.</p>}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
